import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiSave,
  FiX,
  FiBriefcase,
  FiInfo,
  FiMapPin,
  FiGlobe,
} from "react-icons/fi";
import { toast } from "react-hot-toast";
import useClients from "../../../Services/ClientServices/ClientConfig/useClientConfig";
import { GlobalContext } from "../../../../Context/GlobalContext";
import TemplateSearchSelect from "../../../Domain/TemplateSearchSelect";
import { uploadItemImage } from "../../../../Utils/imageUploadUtils";

const THEME_BLUE_BG = "bg-[#f8fbff]";
const BORDER_SMART = "border-gray-300";

const COUNTRY_CODES = [
  { code: "IN", label: "India" },
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
  { code: "CA", label: "Canada" },
  { code: "AE", label: "United Arab Emirates" },
  { code: "SG", label: "Singapore" },
  { code: "UZ", label: "Uzbekistan" },
  { code: " ", label: "None" },
];

export default function MerchantInfoSection({ clientId, clientName }) {
  const { api, anlookApi, adminId } = useContext(GlobalContext);

  const { clients, fetchMerchantInfo, updateMerchantInfo } = useClients();

  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
const [templates, setTemplates] = useState([]);
const [templatesLoading, setTemplatesLoading] = useState(false);

  const client = useMemo(() => {
    if (!clients?.length || !clientId) return null;

    return clients.find(
      (c) =>
        String(c.us_code) === String(clientId) ||
        c.organization_name === clientName,
    );
  }, [clients, clientId, clientName]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!adminId) {
        console.error("Admin/User ID missing for template fetch");
        return;
      }

      try {
        setTemplatesLoading(true);

        const response = await api.post("/Admin/v1/getAllTemplates", {
          userid: adminId,
        });

        if (response?.data?.responseCode === "00") {
          const templateList = Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

          setTemplates(templateList);
        } else {
          setTemplates([]);

          toast.error(
            response?.data?.responseMessage || "Failed to fetch templates",
          );
        }
      } catch (error) {
        console.error("getAllTemplates error:", error?.response?.data || error);

        setTemplates([]);

        toast.error("Something went wrong while fetching templates");
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, [api, adminId]);

  useEffect(() => {
    if (!clientId) return;

    const load = async () => {
      setLoading(true);

      const d = await fetchMerchantInfo(clientId);

      if (d) {
        setForm({
          merchant_name: d.merchant_name || "",
          email_address: d.email_address || "",
          contact_phone: d.contact_phone || "",
          merchant_image: d.merchant_logo || "",
          description: d.description || "",
          theme_id: String(d.theme_id || "1"),
          street: d.street || "",
          city: d.city || "",
          state: d.state || "",
          post_code: d.post_code || "",
          country_code: d.country_code || "",
        });
      }

      setLoading(false);
    };

    load();
  }, [clientId]);

  const handleSave = async () => {
    setSaving(true);

    const success = await updateMerchantInfo({
      userid: clientId,
      ...form,
    });

    if (success) setEditMode(false);

    setSaving(false);
  };

const handleLogoUpload = async (event) => {
  const file = event.target.files?.[0];

  if (!file) return;

  if (!clientId) {
    toast.error("Client id missing");
    event.target.value = "";
    return;
  }

  const toastId = toast.loading("Uploading logo...");

  try {
    const result = await uploadItemImage({
      anlookApi,
      userid: clientId,
      itemName: "logo",
      file,
      maxSizeMB: 2,
    });

    const imageUrl = result?.imageUrl;

    if (!imageUrl) {
      throw new Error("Image URL missing");
    }

    setForm((previousForm) => ({
      ...previousForm,
      merchant_image: imageUrl,
    }));

    toast.success("Logo uploaded", {
      id: toastId,
    });
  } catch (error) {
    console.error("Logo upload error:", error);

    toast.error(error?.message || "Logo upload failed", {
      id: toastId,
    });
  } finally {
    event.target.value = "";
  }
};

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#01C37D]/20 border-t-[#01C37D]" />
      </div>
    );
  }

  return (
    <div className={`w-full ${THEME_BLUE_BG}`}>
      <main className="grid max-w-[1600px] gap-3 px-0 py-2 lg:grid-cols-[1fr_280px]">
        <div className="space-y-3">
          <Card
            title="Business Identity"
            icon={FiBriefcase}
            rightAction={
              !editMode ? (
                <button
                  type="button"
                  onClick={() => setEditMode(true)}
                  className={`inline-flex h-7 items-center gap-1 rounded-md border ${BORDER_SMART} bg-white px-2.5 text-[10px] font-black uppercase tracking-wide text-gray-700 hover:bg-gray-50`}
                >
                  <FiEdit2 size={11} />
                  Edit
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex h-7 items-center gap-1 rounded-md bg-[#01C37D] px-2.5 text-[10px] font-black uppercase tracking-wide text-white disabled:opacity-50"
                  >
                    <FiSave size={11} />
                    {saving ? "Saving" : "Save"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditMode(false)}
                    className={`inline-flex h-7 items-center gap-1 rounded-md border ${BORDER_SMART} bg-white px-2 text-[10px] font-black uppercase tracking-wide text-rose-500`}
                  >
                    <FiX size={11} />
                    Cancel
                  </button>
                </div>
              )
            }
          >
            <div className="grid gap-2 md:grid-cols-3">
              <Field label="Merchant Name">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.merchant_name || ""}
                  onChange={(e) =>
                    setForm({ ...form, merchant_name: e.target.value })
                  }
                />
              </Field>

              <Field label="Contact Phone">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.contact_phone || ""}
                  onChange={(e) =>
                    setForm({ ...form, contact_phone: e.target.value })
                  }
                />
              </Field>

              <Field label="Support Email">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.email_address || ""}
                  onChange={(e) =>
                    setForm({ ...form, email_address: e.target.value })
                  }
                />
              </Field>

              <Field label="TagLine">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.description || ""}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Field>
              
              <Field label="Theme ID">
                <TemplateSearchSelect
                  templates={templates}
                  value={String(form.theme_id || "")}
                  loading={templatesLoading}
                  disabled={!editMode || templatesLoading || saving}
                  placeholder={
                    templatesLoading ? "Loading templates..." : "Select theme"
                  }
                  onChange={(template) => {
                    setForm((prev) => ({
                      ...prev,
                      theme_id: String(template?.id || ""),
                    }));
                  }}
                />
              </Field>

              <Field label="Public Logo">
                <div className="flex items-center gap-2">
                  {form.merchant_image ? (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-300 bg-white">
                      <img
                        src={form.merchant_image}
                        alt="Logo"
                        className="h-full w-full object-contain p-1"
                      />
                    </div>
                  ) : (
                    <div className="h-14 w-14 shrink-0 rounded-lg border border-gray-300 bg-gray-50" />
                  )}

                  <div className="flex h-14 flex-1 items-center">
                    <input
                      type="file"
                      accept="image/*"
                      disabled={!editMode}
                      className={`h-8 w-full rounded-md border px-2 pt-1 text-[11px] font-semibold outline-none transition file:mr-2 file:rounded-md file:border-0 file:bg-gray-100 file:px-2 file:py-1 file:text-[10px] file:font-bold file:text-gray-600 ${
                        editMode
                          ? "bg-white border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/10"
                          : "bg-[#f8fbff] border-gray-300 cursor-not-allowed text-gray-600"
                      }`}
                      onChange={handleLogoUpload}
                    />
                  </div>
                </div>
              </Field>
            </div>
          </Card>

          <Card title="Registered Address" icon={FiMapPin}>
            <div className="grid gap-2 md:grid-cols-4">
              <div className="md:col-span-2">
                <Field label="Street / Floor / Building">
                  <input
                    className={inputCls(editMode)}
                    disabled={!editMode}
                    value={form.street || ""}
                    onChange={(e) =>
                      setForm({ ...form, street: e.target.value })
                    }
                  />
                </Field>
              </div>

              <Field label="City">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.city || ""}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </Field>

              <Field label="State">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.state || ""}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </Field>

              <Field label="Postal Code">
                <input
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.post_code || ""}
                  onChange={(e) =>
                    setForm({ ...form, post_code: e.target.value })
                  }
                />
              </Field>

              <Field label="Country Code">
                <select
                  className={inputCls(editMode)}
                  disabled={!editMode}
                  value={form.country_code || ""}
                  onChange={(e) =>
                    setForm({ ...form, country_code: e.target.value })
                  }
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} - {c.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Card>
        </div>

        <aside className="space-y-3">
          <div className={`rounded-xl border ${BORDER_SMART} bg-white p-3`}>
            <div className="mb-2 text-[10px] font-black uppercase tracking-wider text-gray-400">
              Logo Asset Preview
            </div>

            <div
              className={`flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border ${BORDER_SMART} ${THEME_BLUE_BG}`}
            >
              {form.merchant_image ? (
                <img
                  src={form.merchant_image}
                  alt="Logo"
                  className="h-full w-full object-contain p-4"
                />
              ) : (
                <FiGlobe size={34} className="text-blue-200" />
              )}
            </div>

            <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 p-2">
              <div className="mb-1 flex items-center gap-1.5 text-emerald-700">
                <FiInfo size={12} />
                <span className="text-[10px] font-extrabold uppercase tracking-wide">
                  System Sync
                </span>
              </div>

              <p className="text-[10px] font-semibold leading-4 text-emerald-800">
                Identity data is shared across invoices and public links.
              </p>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

/* ---------------- HELPERS ---------------- */

const inputCls = (editMode) => `
  h-8 w-full rounded-md border px-2 text-[11px] font-semibold outline-none transition
  ${
    editMode
      ? "bg-white border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/10"
      : "bg-[#f8fbff] border-gray-300 cursor-not-allowed text-gray-600"
  }
`;

function Card({ title, icon: Icon, rightAction, children }) {
  return (
    <div className={`rounded-xl bg-white p-3 border ${BORDER_SMART}`}>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${THEME_BLUE_BG} text-[#01C37D]`}
          >
            <Icon size={14} />
          </div>

          <h3 className="truncate text-[10px] font-black uppercase tracking-wider text-gray-400">
            {title}
          </h3>
        </div>

        {rightAction}
      </div>

      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="ml-0.5 text-[9px] font-extrabold uppercase tracking-wide text-gray-500">
        {label}
      </label>

      {children}
    </div>
  );
}
